/**
 * Reversi Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → move placement →
 * disc flipping → turn management → game over.
 *
 * Uses UniversalGameLayout for consistent layout across all games.
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { GameCoachService } from '../../services/GameCoachService';
import { reversiKnowledge } from '../../services/gameCoachKnowledge';
import { GameCoach } from '../../components/GameCoach';
import { ReversiEngine } from '../ReversiEngine';
import { ReversiAI } from '../ReversiAI';
import { ReversiBoard } from './ReversiBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { ReversiErrorHandler } from '../services/ReversiErrorHandler';
import { ReversiStatisticsService } from '../services/ReversiStatisticsService';
import { ReversiGamePersistence } from '../services/ReversiGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  ReversiGameSettings,
  ReversiGameState,
  PlayerColor,
  Position,
  ReversiGameProps,
} from '../types';
import './ReversiGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const ReversiGameInner: React.FC<ReversiGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<ReversiGameSettings | null>(null);
  const [gameState, setGameState] = useState<ReversiGameState | null>(null);
  const [animating, setAnimating] = useState(false);
  const [lastMovePos, setLastMovePos] = useState<Position | null>(null);

  // Refs
  const engineRef = useRef<ReversiEngine>(new ReversiEngine());
  const aiRef = useRef<ReversiAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const humanColorRef = useRef<PlayerColor>('black');
  const coachRef = useRef(new GameCoachService('Reversi', reversiKnowledge));

  // Services (lazy init)
  const errorHandlerRef = useRef<ReversiErrorHandler | null>(null);
  const statsServiceRef = useRef<ReversiStatisticsService | null>(null);
  const persistenceRef = useRef<ReversiGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new ReversiErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new ReversiStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new ReversiGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // Check for saved game on mount
  useEffect(() => {
    if (persistenceRef.current?.hasAutoSave()) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Saved Game Found',
        message: 'A saved game was found. Start a new game to continue.',
        duration: 4000,
      });
    }
  }, [addNotification]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: ReversiGameSettings) => {
      const engine = engineRef.current;
      engine.initializeGame(newSettings);

      // Determine human color
      const humanColor = newSettings.playerColor || 'black';
      humanColorRef.current = humanColor;

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setLastMovePos(null);
      setAnimating(false);
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('reversi');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new ReversiAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${newSettings.mode === 'singleplayer' ? 'Single player' : 'Multiplayer'} game started!`,
        duration: 2000,
      });

      // If AI goes first (human chose white), trigger AI move
      if (
        newSettings.mode === 'singleplayer' &&
        humanColor === 'white' &&
        aiRef.current
      ) {
        const state = engine.getGameState();
        aiRef.current.scheduleMove(state.board, state.currentPlayer, (pos) => {
          if (pos) handleAIMove(pos);
        });
      }
    },
    [addNotification]
  );

  const handleCellClick = useCallback(
    (position: Position) => {
      const engine = engineRef.current;
      if (engine.isGameOver() || animating) return;

      const currentPlayer = engine.getCurrentPlayer();

      // In single player, only allow human to click
      if (settings?.mode === 'singleplayer') {
        if (currentPlayer !== humanColorRef.current) return;
      }

      executeMove(currentPlayer, position);
    },
    [animating, settings]
  );

  const executeMove = useCallback(
    (player: PlayerColor, position: Position) => {
      const engine = engineRef.current;

      try {
        const result = engine.executeMove(player, position);
        setLastMovePos(position);
        setAnimating(true);

        // Show flip count notification
        if (result.flippedDiscs.length > 0) {
          addNotification({
            type: NotificationType.INFO,
            title: `${player === 'black' ? '⚫' : '⚪'} ${player.charAt(0).toUpperCase() + player.slice(1)}`,
            message: `Flipped ${result.flippedDiscs.length} disc${result.flippedDiscs.length > 1 ? 's' : ''}!`,
            duration: 1500,
          });
        }

        // After flip animation, advance turn
        setTimeout(() => {
          setAnimating(false);

          // Advance turn
          engine.advanceTurn();
          const newState = engine.getGameState();
          setGameState(newState);

          // Check if game is over
          if (engine.isGameOver()) {
            handleGameOver(newState);
            return;
          }

          // Check if turn was skipped
          if (newState.currentPlayer === player) {
            const opponent = player === 'black' ? 'white' : 'black';
            addNotification({
              type: NotificationType.WARNING,
              title: 'Turn Skipped',
              message: `${opponent.charAt(0).toUpperCase() + opponent.slice(1)} has no valid moves. ${player.charAt(0).toUpperCase() + player.slice(1)} plays again!`,
              duration: 2500,
            });
          }

          // Auto-save
          if (persistenceRef.current && settings) {
            persistenceRef.current.autoSave(settings.mode, newState, {
              aiDifficulty: settings.aiDifficulty,
              startedAt: newState.startedAt,
              lastSavedAt: Date.now(),
            });
          }

          // If next player is AI, schedule move
          if (
            settings?.mode === 'singleplayer' &&
            aiRef.current &&
            newState.currentPlayer !== humanColorRef.current &&
            !engine.isGameOver()
          ) {
            aiRef.current.scheduleMove(
              newState.board,
              newState.currentPlayer,
              (pos) => {
                if (pos) handleAIMove(pos);
              }
            );
          }
        }, 500);
      } catch (error) {
        setAnimating(false);
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification, settings]
  );

  const handleAIMove = useCallback(
    (position: Position) => {
      const engine = engineRef.current;
      if (engine.isGameOver()) return;

      const currentPlayer = engine.getCurrentPlayer();
      executeMove(currentPlayer, position);
    },
    [executeMove]
  );

  const handleGameOver = useCallback(
    (state: ReversiGameState) => {
      setGameState(state);
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settings) {
        const totalFlips = state.moveHistory.length > 0
          ? state.score.black + state.score.white - 4 // Total discs minus starting 4
          : 0;

        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: settings.mode,
            difficulty: settings.aiDifficulty,
            winner: state.winner,
            score: state.score,
            totalMoves: state.moveHistory.length,
            totalFlips,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          humanColorRef.current,
          isAuthenticated ? user?.id : undefined
        );
      }

      // Show result notification
      const winner = state.winner;
      if (winner === 'draw') {
        addNotification({
          type: NotificationType.INFO,
          title: '🤝 Draw!',
          message: `Game ended in a draw! ${state.score.black} - ${state.score.white}`,
          duration: 5000,
        });
      } else {
        addNotification({
          type: NotificationType.SUCCESS,
          title: '🎉 Game Over!',
          message: `${winner === 'black' ? '⚫ Black' : '⚪ White'} wins ${state.score.black} - ${state.score.white}!`,
          duration: 5000,
        });
      }
    },
    [addNotification, settings, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setLastMovePos(null);
    setSettings(null);
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Trigger AI on initial turn if AI goes first
  // =========================================================================
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    const engine = engineRef.current;
    const currentPlayer = engine.getCurrentPlayer();
    if (
      currentPlayer !== humanColorRef.current &&
      !engine.isGameOver() &&
      !animating
    ) {
      aiRef.current.scheduleMove(gameState.board, currentPlayer, (pos) => {
        if (pos) handleAIMove(pos);
      });
    }
    // Only run when view changes to playing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const reversiConfig: GameModeConfig = {
      gameName: 'Reversi',
      gameIcon: '⚪',
      availableModes: ['ai', 'local', 'online'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '☀️', description: 'AI makes mistakes' },
        { id: 'medium', label: 'Medium', icon: '⭐', description: 'Balanced gameplay' },
        { id: 'hard', label: 'Hard', icon: '🏆', description: 'Expert-level AI' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai' && selection.difficulty) {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: selection.difficulty as ReversiGameSettings['aiDifficulty'],
        });
      } else if (selection.mode === 'local') {
        handleStartGame({ mode: 'local-multiplayer' });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="reversi-game-container">
        <UniversalGameModeSelector
          config={reversiConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const currentPlayer = gameState.currentPlayer;
  const isHumanTurn =
    settings?.mode !== 'singleplayer' || currentPlayer === humanColorRef.current;
  const isBoardDisabled = !isHumanTurn || animating || gameState.gameStatus === 'completed';
  const isGameOver = gameState.gameStatus === 'completed';
  const isAIThinking = !isHumanTurn && settings?.mode === 'singleplayer' && !isGameOver;

  // =========================================================================
  // Build UniversalGameLayout config
  // =========================================================================

  // Determine game-over state
  const gameOverResult = (() => {
    if (!isGameOver || !gameState.winner) return 'draw' as const;
    if (gameState.winner === 'draw') return 'draw' as const;
    if (settings?.mode === 'singleplayer') {
      return gameState.winner === humanColorRef.current ? 'win' as const : 'loss' as const;
    }
    return 'win' as const;
  })();

  const gameOverTitle = (() => {
    if (!isGameOver) return '';
    if (gameState.winner === 'draw') return '🤝 Draw!';
    if (settings?.mode === 'singleplayer') {
      return gameState.winner === humanColorRef.current ? '🎉 Victory!' : '😔 Defeat!';
    }
    return `🎉 ${gameState.winner === 'black' ? 'Black' : 'White'} Wins!`;
  })();

  const gameOverSubtitle = (() => {
    if (!isGameOver) return '';
    return `Final Score: ⚫ ${gameState.score.black} — ⚪ ${gameState.score.white}`;
  })();

  const gameOverIcon = (() => {
    if (gameOverResult === 'win') return '🏆';
    if (gameOverResult === 'loss') return '😞';
    return '🤝';
  })();

  // Left panels: Score + Move Info
  const leftPanels = [
    {
      id: 'score',
      title: 'Score',
      icon: '📊',
      content: (
        <div className="reversi-score-panel">
          <div className={`reversi-score-panel__row ${currentPlayer === 'black' ? 'reversi-score-panel__row--active' : ''}`}>
            <span className="reversi-score-panel__disc reversi-score-panel__disc--black" />
            <span className="reversi-score-panel__label">Black</span>
            <span className="reversi-score-panel__value">{gameState.score.black}</span>
          </div>
          <div className={`reversi-score-panel__row ${currentPlayer === 'white' ? 'reversi-score-panel__row--active' : ''}`}>
            <span className="reversi-score-panel__disc reversi-score-panel__disc--white" />
            <span className="reversi-score-panel__label">White</span>
            <span className="reversi-score-panel__value">{gameState.score.white}</span>
          </div>
          <div className="reversi-score-panel__info">
            <span>Moves: {gameState.moveHistory.length}</span>
            {gameState.validMoves.length > 0 && isHumanTurn && (
              <span>{gameState.validMoves.length} valid move{gameState.validMoves.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'history',
      title: 'Move History',
      icon: '📜',
      content: (
        <div className="reversi-history-panel">
          {gameState.moveHistory.length === 0 ? (
            <div className="reversi-history-panel__empty">No moves yet</div>
          ) : (
            <ol className="reversi-history-panel__list">
              {gameState.moveHistory.map((move, idx) => (
                <li key={idx} className="reversi-history-panel__item">
                  <span className="reversi-history-panel__number">{idx + 1}.</span>
                  <span className="reversi-history-panel__player">
                    {move.player === 'black' ? '⚫' : '⚪'}
                  </span>
                  <span className="reversi-history-panel__pos">
                    ({move.position.row}, {move.position.col})
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ),
    },
  ];

  // Right panels: AI Coach in single-player mode
  const rightPanels = settings?.mode === 'singleplayer' ? [
    {
      id: 'coach',
      title: 'AI Coach',
      icon: '🤖',
      content: (
        <GameCoach
          coach={coachRef.current}
          gameContext={{
            gameName: 'Reversi',
            gameState: `Score: Black ${gameState.score.black} - White ${gameState.score.white}. ${gameState.validMoves.length} valid moves available. Move ${gameState.moveHistory.length}.`,
            playerContext: `Playing as ${humanColorRef.current}. ${isHumanTurn ? "It's your turn." : "Waiting for AI."}`,
          }}
          isPlayerTurn={isHumanTurn}
          quickQuestions={['Corner strategy?', 'Best move?', 'Mobility tips?', 'Edge play?']}
          accentColor="#166534"
        />
      ),
    },
  ] : [];

  // Status bar
  const statusBar = {
    turnIndicator: (
      <span>
        {currentPlayer === 'black' ? '⚫' : '⚪'}{' '}
        {isGameOver
          ? 'Game Over'
          : isAIThinking
            ? 'AI thinking...'
            : `${currentPlayer === 'black' ? 'Black' : 'White'}'s turn`}
      </span>
    ),
    gameStatus: isGameOver
      ? (gameState.winner === 'draw' ? '🤝 Draw' : '👑 Game Over')
      : undefined,
    gameStatusType: isGameOver ? 'ended' as const : 'active' as const,
    aiThinking: isAIThinking,
  };

  // Actions
  const actions = [
    {
      id: 'new-game',
      label: 'New Game',
      icon: '🔄',
      onClick: handleNewGame,
      variant: 'primary' as const,
    },
    ...(onExit ? [{
      id: 'exit',
      label: 'Exit',
      icon: '🚪',
      onClick: handleExit,
      variant: 'secondary' as const,
    }] : []),
  ];

  // Game over config
  const gameOver = (view === 'game-over' && isGameOver) ? {
    show: true,
    result: gameOverResult,
    title: gameOverTitle,
    subtitle: gameOverSubtitle,
    icon: gameOverIcon,
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Reversi',
    board: (
      <ReversiBoard
        board={gameState.board}
        validMoves={isHumanTurn ? gameState.validMoves : []}
        lastFlipped={gameState.lastFlipped}
        lastMove={lastMovePos}
        currentPlayer={currentPlayer}
        onCellClick={handleCellClick}
        disabled={isBoardDisabled}
        animating={animating}
      />
    ),
    leftPanels,
    rightPanels,
    statusBar,
    actions,
    gameOver,
  };

  return (
    <div className="reversi-game-container">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const ReversiGame: React.FC<ReversiGameProps> = (props) => (
  <ErrorBoundary componentName="Reversi">
    <ReversiGameInner {...props} />
  </ErrorBoundary>
);

export { ReversiGame };
export default ReversiGame;
